import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

export default function ApplyFormPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { applicantType, runId, result } = location.state || {};
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ firstname: '', lastname: '', phone: '', email: '', postalCode: '' });
  const [interests, setInterests] = useState([]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!applicantType) { navigate('/'); return; }
    api.get('/config/public').then(r => setConfig(r.data));
  }, [applicantType, navigate]);

  if (!config) return null;

  const interestOptions = applicantType === 'azubi' ? config.azubiInterests : config.profiInterests;
  const isMulti = applicantType === 'azubi' ? config.azubiInterestMultiSelect : config.profiInterestMultiSelect;

  const toggleInterest = (item) => {
    if (isMulti) {
      setInterests(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
    } else {
      setInterests([item]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstname || !form.lastname || !form.phone || !form.email || !form.postalCode) {
      toast.error('Bitte alle Pflichtfelder ausfüllen'); return;
    }
    if (interests.length === 0) { toast.error('Bitte Interesse wählen'); return; }
    if (!consent) { toast.error('DSGVO-Zustimmung erforderlich'); return; }
    setLoading(true);
    try {
      await api.post('/lead/submit', {
        runId, applicantType, ...form, interests, consent: true
      });
      navigate('/apply/thanks');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    }
    setLoading(false);
  };

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-8" data-testid="apply-form-page">
      <div className="animate-fade-up glass-card rounded-2xl p-6 sm:p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-outfit font-bold ${applicantType === 'azubi' ? 'bg-[#3B82F6]/15 text-[#60A5FA]' : 'bg-[#8B5CF6]/15 text-[#A78BFA]'}`}>
            {applicantType === 'azubi' ? 'Auszubildende/r' : 'Berufserfahren'}
          </span>
          <h2 className="font-outfit font-bold text-2xl text-white mt-3">Bewerbung</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#9CA3AF] text-xs">Vorname *</Label>
              <Input value={form.firstname} onChange={e => setForm(f => ({...f, firstname: e.target.value}))}
                className="bg-white/5 border-white/10 text-white mt-1" data-testid="input-firstname" />
            </div>
            <div>
              <Label className="text-[#9CA3AF] text-xs">Nachname *</Label>
              <Input value={form.lastname} onChange={e => setForm(f => ({...f, lastname: e.target.value}))}
                className="bg-white/5 border-white/10 text-white mt-1" data-testid="input-lastname" />
            </div>
          </div>

          <div>
            <Label className="text-[#9CA3AF] text-xs">Telefon *</Label>
            <Input type="tel" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
              className="bg-white/5 border-white/10 text-white mt-1" data-testid="input-phone" />
          </div>

          <div>
            <Label className="text-[#9CA3AF] text-xs">E-Mail *</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
              className="bg-white/5 border-white/10 text-white mt-1" data-testid="input-email" />
          </div>

          <div>
            <Label className="text-[#9CA3AF] text-xs">PLZ *</Label>
            <Input value={form.postalCode} onChange={e => setForm(f => ({...f, postalCode: e.target.value.slice(0, 5)}))}
              maxLength={5} className="bg-white/5 border-white/10 text-white mt-1 w-32" data-testid="input-postalCode" />
          </div>

          <div>
            <Label className="text-[#9CA3AF] text-xs mb-2 block">
              {applicantType === 'azubi' ? 'Interesse an Ausbildung' : 'Interesse an Bereich'} *
            </Label>
            <div className="space-y-2">
              {interestOptions.map((item) => (
                <button key={item} type="button" onClick={() => toggleInterest(item)}
                  data-testid={`interest-${item}`}
                  className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all
                    ${interests.includes(item)
                      ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-white'
                      : 'border-white/10 bg-white/3 text-[#9CA3AF] hover:bg-white/5'}`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox checked={consent} onCheckedChange={setConsent} id="consent"
              className="border-white/20 data-[state=checked]:bg-[#3B82F6] data-[state=checked]:border-[#3B82F6] mt-0.5"
              data-testid="consent-checkbox" />
            <label htmlFor="consent" className="text-xs text-[#9CA3AF] leading-relaxed cursor-pointer">
              Ich stimme der Verarbeitung meiner Daten gemäß der <a href="https://datenschutzportal.de/juunit/" target="_blank" rel="noopener noreferrer" className="underline">Datenschutzerklärung</a> zu.
            </label>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-outfit font-bold rounded-xl mt-4" data-testid="submit-application-btn">
            <Send className="w-4 h-4 mr-2" /> {loading ? 'Wird gesendet...' : 'Bewerbung absenden'}
          </Button>
        </form>
      </div>
    </div>
  );
}
